/**
 * RatesSection Component
 * Displays live market rates for Gold 24K, Gold 22K, and Silver with real-time price changes
 */

import React from 'react';
import { PriceChange } from '../types/metalRates';
import { formatRelativeTime } from '../utils/dateFormat';

interface RatesSectionProps {
  goldRate24K: number | null;
  goldRate22K: number | null;
  silverRate: number | null;
  goldChange: PriceChange | null;
  silverChange: PriceChange | null;
  timestamp: Date | null;
  error: string | null;
  usingFallback: boolean;
}

export const RatesSection: React.FC<RatesSectionProps> = ({
  goldRate24K,
  goldRate22K,
  silverRate,
  goldChange,
  silverChange,
  timestamp,
  error,
  usingFallback,
}) => {
  // Calculate 22K change from 24K change (if available)
  const gold22KChange = goldChange ? {
    amount: goldChange.amount * 0.916,
    percentage: goldChange.percentage, // Percentage stays the same
    direction: goldChange.direction,
  } : null;

  const lastUpdate = timestamp ? formatRelativeTime(timestamp) : 'N/A';

  return (
    <section id="rates" className="py-20 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-center text-white mb-6">
          Live Market Rates
        </h2>

        {/* Description */}
        <p className="text-gray-400 text-center text-lg mb-12 max-w-3xl mx-auto">
          Real-time updates directly from the bullion market to ensure transparency 
          and trust in every purchase.
        </p>

        {/* Rate Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Gold 24K Card */}
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-2xl p-8 backdrop-blur-sm relative">
            {/* Error Badge */}
            {usingFallback && (
              <div className="absolute top-4 right-4">
                <span className="bg-yellow-600/20 text-yellow-500 text-xs font-semibold px-2 py-1 rounded-full border border-yellow-600/30">
                  ⚠ Cached
                </span>
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Gold 24K</h3>
              <span className="bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full uppercase">
                99.9% Purity
              </span>
            </div>

            {/* Price */}
            <div className="mb-4">
              <p className="text-5xl font-bold text-white mb-2">
                ₹{goldRate24K ? goldRate24K.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '15,998.00'}
              </p>
              <p className="text-gray-500 text-sm">Per Gram (Current)</p>
            </div>

            {/* Change */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
              <div className="flex items-center space-x-2">
                {goldChange ? (
                  <span className={`text-lg font-semibold ${goldChange.direction === 'up' ? 'text-green-500' : goldChange.direction === 'down' ? 'text-red-500' : 'text-gray-500'}`}>
                    {goldChange.direction === 'up' ? '↗' : goldChange.direction === 'down' ? '↘' : '→'} 
                    {goldChange.direction !== 'neutral' && (goldChange.amount >= 0 ? '+' : '')}
                    ₹{Math.abs(goldChange.amount).toFixed(2)}
                  </span>
                ) : (
                  <span className="text-lg font-semibold text-gray-500">N/A</span>
                )}
              </div>
              <p className="text-gray-600 text-xs">Last update: {lastUpdate}</p>
            </div>
          </div>

          {/* Gold 22K Card */}
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-2xl p-8 backdrop-blur-sm relative">
            {/* Error Badge */}
            {usingFallback && (
              <div className="absolute top-4 right-4">
                <span className="bg-yellow-600/20 text-yellow-500 text-xs font-semibold px-2 py-1 rounded-full border border-yellow-600/30">
                  ⚠ Cached
                </span>
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Gold 22K</h3>
              <span className="bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full uppercase">
                91.6% Purity
              </span>
            </div>

            {/* Price */}
            <div className="mb-4">
              <p className="text-5xl font-bold text-white mb-2">
                ₹{goldRate22K ? goldRate22K.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '14,654.00'}
              </p>
              <p className="text-gray-500 text-sm">Per Gram (Current)</p>
            </div>

            {/* Change */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
              <div className="flex items-center space-x-2">
                {gold22KChange ? (
                  <span className={`text-lg font-semibold ${gold22KChange.direction === 'up' ? 'text-green-500' : gold22KChange.direction === 'down' ? 'text-red-500' : 'text-gray-500'}`}>
                    {gold22KChange.direction === 'up' ? '↗' : gold22KChange.direction === 'down' ? '↘' : '→'} 
                    {gold22KChange.direction !== 'neutral' && (gold22KChange.amount >= 0 ? '+' : '')}
                    ₹{Math.abs(gold22KChange.amount).toFixed(2)}
                  </span>
                ) : (
                  <span className="text-lg font-semibold text-gray-500">N/A</span>
                )}
              </div>
              <p className="text-gray-600 text-xs">Last update: {lastUpdate}</p>
            </div>
          </div>

          {/* Silver Card */}
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-2xl p-8 backdrop-blur-sm relative">
            {/* Error Badge */}
            {usingFallback && (
              <div className="absolute top-4 right-4">
                <span className="bg-yellow-600/20 text-yellow-500 text-xs font-semibold px-2 py-1 rounded-full border border-yellow-600/30">
                  ⚠ Cached
                </span>
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Silver</h3>
              <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                99.9% Purity
              </span>
            </div>

            {/* Price */}
            <div className="mb-4">
              <p className="text-5xl font-bold text-white mb-2">
                ₹{silverRate ? silverRate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '302.52'}
              </p>
              <p className="text-gray-500 text-sm">Per Gram (Current)</p>
            </div>

            {/* Change */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
              <div className="flex items-center space-x-2">
                {silverChange ? (
                  <span className={`text-lg font-semibold ${silverChange.direction === 'up' ? 'text-green-500' : silverChange.direction === 'down' ? 'text-red-500' : 'text-gray-500'}`}>
                    {silverChange.direction === 'up' ? '↗' : silverChange.direction === 'down' ? '↘' : '→'} 
                    {silverChange.direction !== 'neutral' && (silverChange.amount >= 0 ? '+' : '')}
                    ₹{Math.abs(silverChange.amount).toFixed(2)}
                  </span>
                ) : (
                  <span className="text-lg font-semibold text-gray-500">N/A</span>
                )}
              </div>
              <p className="text-gray-600 text-xs">Last update: {lastUpdate}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
